<template>
  <!-- Table Container Card -->
  <b-card no-body>

    <div class="m-2">
      <b-row>
        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />
          <b-dropdown
            v-if="getUserPermission('EXPORT_CARD_REQUEST_LOG')"
            id="dropdown-1"
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            :text="$t('Export')"
            variant="primary"
          >
            <b-dropdown-item @click="csvExport(csvData)">
              {{ $t('CSV') }}
            </b-dropdown-item>
            <b-dropdown-item @click="excelExport(csvData)">
              {{ $t('Excel') }}
            </b-dropdown-item>
          </b-dropdown>
        </b-col>
        <!-- Export -->
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-lg-end">
            <Button
              v-if="selectedPendingIds.length !== 0"
              :label="$t('Process Pending Request')"
              size="md"
              class="mr-1"
              @click="onProcessPendingRequest"
            />
            <Button
              :label="$t('Filter')"
              size="md"
              icon="FilterIcon"
              show-icon
              class="mr-1"
              @click="showModal('modal-filter')"
            />
            <Button
              :label="$t('Reset Filter')"
              size="md"
              icon="RotateCwIcon"
              show-icon
              @click="resetFilter"
            />
          </div>
        </b-col>
      </b-row>
    </div>

    <b-table
      ref="glTableRef"
      :items="cardRequestData"
      :busy="isBusy"
      :per-page="pageSize"
      :current-page="currentPage"
      responsive
      :fields="translatedTableHeaders"
      primary-key="id"
      show-empty
      :empty-text="$t('No records available')"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      class="position-relative"
    >

      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>

      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>
      <template #cell(select)="data">
        <b-form-group class="mt-2">
          <b-form-checkbox
            v-if="data.item.status === 'Pending'"
            v-model="selectedPendingIds"
            :value="data.item.id"
          />
        </b-form-group>
      </template>
      <template #cell(requestDate)="data">
        <p class="mb-0">
          {{ data.value ? formatCustomDate(data.value) : '' }}
        </p>
      </template>
      <template #cell(status)="data">
        <b-badge
          pill
          :variant="getStatusColor(data.value)"
        >
          {{ data.value }}
        </b-badge>
      </template>
    </b-table>

    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />

    <!-- modal -->
    <b-modal
      id="modal-filter"
      ref="my-modal"
      :ok-title="$t('Apply')"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      size="lg"
      no-close-on-backdrop
      :title="$t('Filters')"
      modal-footer
      @ok="handleSubmit"
      @cancel="resetFilters"
    >

      <validation-observer ref="cardRequestRules">
        <b-form>
          <b-row class="match-height">
            <!-- Account Number -->
            <b-col md="6">
              <InputField
                id="account-number"
                v-model="filter.accountNumber"
                :label="$t('Account Number')"
                :placeholder="$t('Enter Account Number')"
                type="text"
              />
            </b-col>
            <!-- Account Number -->
            <!-- Account Name -->
            <b-col md="6">
              <InputField
                id="account-name"
                v-model="filter.accountName"
                :label="$t('Account Name')"
                :placeholder="$t('Enter Account Name')"
                type="text"
              />
            </b-col>
            <!-- Account Name -->
          </b-row>

          <b-row class="match-height">
            <!-- Card Type -->
            <b-col md="6">
              <SelectField
                id="card-type"
                v-model="filter.cardType"
                :reduce="option => option.value"
                :label="$t('Card Type')"
                :placeholder="$t('Select')"
                :options="translatedCardTypeOptions"
                item="label"
              />
            </b-col>
            <!-- Card Type -->
            <!-- Request Channel -->
            <b-col md="6">
              <SelectField
                id="request-channel"
                v-model="filter.requestChannel"
                :reduce="option => option.value"
                :label="$t('Request Channel')"
                :placeholder="$t('Select')"
                :options="translatedRequestChannelOptions"
                item="label"
              />
            </b-col>
            <!-- Request Channel -->
            <!-- Request Date From -->
            <b-col md="6">
              <DatePickerField
                id="request-date-from"
                v-model="filter.requestDateFrom"
                :min-date="new Date()"
                :label="$t('Request Date (From)')"
                :config="datePickerConfig"
                :placeholder="$t('Request Date From')"
              />
            </b-col>
            <!-- Request Date From -->
            <!-- Request Date To-->
            <b-col md="6">
              <DatePickerField
                id="request-date-to"
                v-model="filter.requestDateTo"
                :min-date="new Date()"
                :label="$t('Request Date (To)')"
                :config="datePickerConfig"
                :placeholder="$t('Request Date To')"
              />
            </b-col>
            <!-- Request Date To-->
            <!-- Status -->
            <b-col md="6">
              <SelectField
                id="status"
                v-model="filter.status"
                :reduce="option => option.value"
                :label="$t('Status')"
                :placeholder="$t('Select')"
                :options="translatedStatusOptions"
                item="label"
              />
            </b-col>
            <!-- Status -->
          </b-row>
        </b-form>
      </validation-observer>

      <template
        v-if="isReadonly"
        #modal-footer="{ }"
      >
        <div />
      </template>
    </b-modal>
    <!-- modal -->

    <FullScreenSpinner :show="showSpinner" />
  </b-card>

</template>

<script>
import {
  BCard, BRow, BCol, BTable, BSpinner, BForm, BBadge,
  BModal, BDropdown, BDropdownItem, BFormCheckbox, BFormGroup,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import { required } from '@validations'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { tableMixins } from '@core/mixins/table'
import { permissionMixin } from '@core/mixins/permission'
import { modalMixins } from '@core/mixins/modal'
import { mapGetters, mapActions } from 'vuex'
import Ripple from 'vue-ripple-directive'
import TablePagination from '@/views/components/table/TablePagination.vue'
import TableEntries from '@/views/components/table/TableEntries.vue'
import InputField from '../../../forms/InputField.vue'
import Button from '../../../components/button/Button.vue'
import SelectField from '../../../forms/SelectField.vue'
import DatePickerField from '../../../forms/DatePickerField.vue'
import toNumber from '../../../../helpers/toNumber'
import getStatusColor from '@/helpers/getStatusColor'
import { toCustomDateTime, toCustomDate } from '../../../../helpers/formatDate'
import { eChannelsPermission } from '../../../../helpers/permissions'
import FullScreenSpinner from '@/views/components/spinner/FullScreenSpinner.vue'

const requestChannelOptions = [
  { value: 'InternetBanking', label: 'Internet Banking' },
  { value: 'Mobile', label: 'Mobile' },
  { value: 'OpenAPI', label: 'OpenAPI' },
]

const statusOptions = [
  { value: 'Pending', label: 'Pending' },
  { value: 'Collected', label: 'Collected' },
  { value: 'Treated', label: 'Treated' },
]

const cardTypeOptions = [
  { value: 'Visa Card', label: 'Visa Card' },
  { value: 'Master Card', label: 'Master Card' },
  { value: 'Verve Card', label: 'Verve Card' },
]

export default {
  components: {
    BCard,
    BRow,
    BCol,
    Button,
    BSpinner,
    BTable,
    BBadge,
    TablePagination,
    TableEntries,
    InputField,
    SelectField,
    BModal,
    BDropdown,
    BDropdownItem,
    BForm,
    ValidationObserver,
    DatePickerField,
    BFormCheckbox,
    BFormGroup,
    FullScreenSpinner,
  },
  directives: {
    Ripple,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      getStatusColor,
      isReadonly: false,
      buttonLabel: this.$t('Save'),
      isEditable: false,
      isBusy: false,
      required,
      modalTitle: this.$t('Card Request Log'),
      sortBy: 'accountName',
      sortDesc: false,
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'select', label: 'Select' },
        { key: 'accountNumber', sortable: true, label: 'Account Number' },
        { key: 'accountName', sortable: true, label: 'Account Name' },
        // { key: 'email', sortable: true, label: 'Email' },
        { key: 'cardType', sortable: true, label: 'Card Type' },
        { key: 'requestChannel', sortable: true, label: 'Request Channel' },
        { key: 'requestDate', sortable: true, label: 'Request Date' },
        { key: 'status', sortable: true, label: 'Status' },
      ],
      filter: {
        accountNumber: '',
        accountName: '',
        cardType: '',
        requestChannel: '',
        requestDateFrom: null,
        requestDateTo: null,
        status: '',
      },
      datePickerConfig: {
        dateFormat: 'Y-m-d',
        maxDate: 'today',
        defaultDate: '',
      },
      // cardTypeOptions: [
      //   'Visa Card',
      //   'Master Card',
      //   'Verve Card',
      // ],
      // requestChannelOptions: [
      //   'Web',
      //   'Mobile',
      // ],
      // statusOptions: [
      //   'Not Download',
      //   'Downloaded',
      // ],
      showSpinner: false,
      cardRequestData: [],
      allSearchedData: [],
      selectedPendingIds: [],
    }
  },
  computed: {
    ...mapGetters({
      branchOptions: 'operations/branchOptions',
      userDetails: 'auth/getCurrentUser',
    }),
    csvData() {
      return this.exportItems.map(item => ({
        ...item,
      }))
    },
    exportItems() {
      return this.allSearchedData.map(item => ({
        'Account Number': item.accountNumber,
        'Account Name': item.accountName,
        Email: item.email,
        'Card Type': item.cardType,
        'Request Channel': item.requestChannel,
        'Request Date': this.formatCustomDate(item.requestDate),
        Status: item.status,
      }))
    },

    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
    translatedRequestChannelOptions() {
      return requestChannelOptions.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
    translatedStatusOptions() {
      return statusOptions.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
    translatedCardTypeOptions() {
      return cardTypeOptions.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
  },
  watch: {
    currentPage(newValue) {
      this.currentPage = newValue
      this.fetchCardRequestLogs()
    },
    perPage(newValue) {
      this.perPage = newValue
      this.fetchCardRequestLogs()
    },
  },
  mounted() {
    this.fetchCardRequestLogs()
  },
  methods: {
    ...mapActions({
      getCardRequestLogs: 'eChannels/getCardRequestLogs',
      performCardPendingRequestAction: 'eChannels/performCardPendingRequestAction',
      downloadCardRequestLogReport: 'reports/downloadCardRequestLogReport',
    }),
    resetFilters() {
      this.filter = { ...null }
    },
    resetFilter() {
      this.filter = { ...null }
      this.fetchCardRequestLogs()
    },
    onProcessPendingRequest() {
      const performPendingRequest = async () => {
        try {
          this.showSpinner = true
          // Check if any items are selected
          if (!this.selectedPendingIds.length) {
            this.alertError({ text: this.$t('Please select at least one item') })
            return
          }
          const formData = {
            ids: this.selectedPendingIds,
          }
          const response = await this.performCardPendingRequestAction({ formData, permission: eChannelsPermission.TREAT_CUSTOMER_CARD_REQUEST })
          if (response && response.status === 200) {
            this.selectedPendingIds = []
            this.alertSuccess({
              text: this.$t('Your request to credit the selected accounts has been received and will be processed shortly.'),
            })
            // Refresh the table data
            this.fetchCardRequestLogs()
          } else {
            this.alertError({
              text: this.$t('Failed to process request. Please try again.'),
            })
          }
        } catch (error) {
          console.error('Error in performPendingRequest:', error)
          this.handleError(error)
        } finally {
          this.showSpinner = false
        }
      }
      this.confirm(performPendingRequest, { title: '', text: this.$t(' Are you sure you want to continue?') })
    },
    async handleSubmit(bvModalEvt) {
      bvModalEvt.preventDefault()
      this.$refs.cardRequestRules.validate().then(async success => {
        if (success) {
          this.fetchCardRequestLogs()
        }
      })
    },
    async fetchCardRequestLogs() {
      try {
        this.hideModal('modal-filter')
        this.isBusy = true
        const response = await this.getCardRequestLogs({
          search: this.filter,
          page: this.currentPage,
          limit: this.perPage,
          permission: eChannelsPermission.VIEW_CARD_REQUEST_LOG,
        })
        const { data, status } = response
        if (status === 200 && data.data) {
          this.isBusy = false
          this.cardRequestData = data.data
          this.totalCount = data.recordCount
        } else {
          this.cardRequestData = []
          this.totalCount = 0
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    async csvExport() {
      try {
        this.isBusy = true
        const response = await this.downloadCardRequestLogReport({
          search: this.filter,
          fileExportFormat: 1,
          permission: eChannelsPermission.EXPORT_CARD_REQUEST_LOG,
        })
        if (response.status === 200) {
          this.alertSuccessCustom({
            text: this.$t('Your download is in progress and might take a little longer. Go to your'),
            link: {
              text: this.$t('Download Manager'),
              route: { name: 'download-manager' },
            },
            afterLinkText: this.$t('to access the completed file.'),
          })
        } else if (response.status === 504) {
          this.alertSuccessCustom({
            text: this.$t('Your download request is still processing, keep checking the'),
            link: {
              text: this.$t('Download Manager'),
              route: { name: 'download-manager' },
            },
            afterLinkText: this.$t('for status.'),
          })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    async excelExport() {
      try {
        this.isBusy = true
        const response = await this.downloadCardRequestLogReport({
          search: this.filter,
          fileExportFormat: 0,
          permission: eChannelsPermission.EXPORT_CARD_REQUEST_LOG,
        })
        if (response.status === 200) {
          this.alertSuccessCustom({
            text: this.$t('Your download is in progress and might take a little longer. Go to your'),
            link: {
              text: this.$t('Download Manager'),
              route: { name: 'download-manager' },
            },
            afterLinkText: this.$t('to access the completed file.'),
          })
        } else if (response.status === 504) {
          this.alertSuccessCustom({
            text: this.$t('Your download request is still processing, keep checking the'),
            link: {
              text: this.$t('Download Manager'),
              route: { name: 'download-manager' },
            },
            afterLinkText: this.$t('for status.'),
          })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    formatNumber: toNumber,
    formatDateTime: toCustomDateTime,
    formatCustomDate: toCustomDate,
  },
}
</script>

<style lang="scss" scoped>
    .per-page-selector {
        width: 90px;
    }

    .invoice-filter-select {
        min-width: 190px;

        ::v-deep .vs__selected-options{
            flex-wrap: nowrap;
        }

        ::v-deep .vs__selected {
            width: 100px;
        }
    }
</style>

<style lang="scss">
    @import '@core/scss/vue/libs/vue-select.scss';
</style>
